import { FechaCR, MontoCRC, MontoUSD } from '@/components/dominio';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useListasContado } from '@/hooks/useListasContado';
import type { ListaContado } from '@adelante/shared';
import { CircleNotch } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

/** "Abierta", "Cerrada" o, cuando todo lo de la lista ya se pagó, "Pagada". */
export function estadoDeLista(l: ListaContado): {
  texto: string;
  variante: 'warning' | 'secondary' | 'success';
} {
  if (l.pagada || (l.cantidadSolicitudes > 0 && l.cantidadPagadas === l.cantidadSolicitudes))
    return { texto: 'Pagada', variante: 'success' };
  if (l.estado === 'ABIERTA') return { texto: 'Abierta', variante: 'warning' };
  return { texto: 'Cerrada', variante: 'secondary' };
}

/** El historial de listas de pagos de contado: una fila por lista, la más reciente primero. */
export function ListasContadoTabla() {
  const { data, isLoading } = useListasContado();
  const navigate = useNavigate();
  const listas = data?.data ?? [];

  if (isLoading)
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
        Cargando…
      </div>
    );

  if (listas.length === 0)
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-muted-foreground">
          Todavía no hay listas de contado. Se crean solas cuando se aprueba la primera solicitud.
        </CardContent>
      </Card>
    );

  return (
    <Card>
      <CardContent className="pt-6">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Lista</TableHead>
              <TableHead>Fecha de pago</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Solicitudes</TableHead>
              <TableHead className="text-right">Solicitado</TableHead>
              <TableHead className="text-right">Aprobado</TableHead>
              <TableHead className="text-right">Archivos</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {listas.map((l) => {
              const e = estadoDeLista(l);
              return (
                <TableRow
                  key={l.idLista}
                  className="cursor-pointer"
                  onClick={() => navigate(`/vencimientos/listas-contado/${l.idLista}`)}
                >
                  <TableCell className="font-medium">{l.nombre}</TableCell>
                  <TableCell>
                    {l.fechaPago ? (
                      <FechaCR fecha={l.fechaPago} />
                    ) : (
                      <span
                        className="text-muted-foreground"
                        title="Todavía no se registra un pago"
                      >
                        —
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={e.variante}>{e.texto}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {l.cantidadSolicitudes}
                    {l.cantidadPagadas > 0 && (
                      <span className="ml-1 text-xs text-muted-foreground">
                        ({l.cantidadPagadas} pagadas)
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {l.totales.map((t) => (
                      <div key={t.moneda}>
                        {t.moneda === 'USD' ? (
                          <MontoUSD monto={t.solicitado} />
                        ) : (
                          <MontoCRC monto={t.solicitado} />
                        )}
                      </div>
                    ))}
                  </TableCell>
                  <TableCell className="text-right">
                    {l.totales.map((t) => (
                      <div key={t.moneda}>
                        {t.moneda === 'USD' ? (
                          <MontoUSD monto={t.aprobado} />
                        ) : (
                          <MontoCRC monto={t.aprobado} />
                        )}
                      </div>
                    ))}
                  </TableCell>
                  <TableCell className="text-right">{l.cantidadArchivos}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
